import { z } from 'zod';
import { AssetCategorySchema, RatingUnitSchema } from './categories.js';
import { AssetConditionGradeSchema, AssetEventReasonTypeSchema } from './events.js';
import { DEVICE_ACCESS_KINDS, DeviceAccessKindSchema, DeviceAccessWriteSchema, IpAddressSchema } from './access.js';
import { CurrencyCodeSchema, refineMoney } from '../common/currency.js';
// Verb signatures — LOCKED at the datamodel phase (ADR-063: one verb, many
// callers — register UI, import, future tickets, external API all call the
// same service verbs; permission + scope + event + cache + audit live INSIDE
// the verb; ceremony lives in wrappers). Implementations land at the register
// backend phase; these shapes are the contract that lets tickets and the
// observation resolve flow arrive without redesign.
//
// 14 verbs: create · move · replace · adjust · edit (audit-only — the one
// verb that writes NO asset_event) · putOnHold · reactivate · retire ·
// reinstate · declareLost · recover · dispose (ops — the physical exit
// asserts custody; the books READ disposed_on, they never write it) + the
// books pair writeOff · transferOwnership.
// Gates: ops verbs incl. dispose = asset:write; the books pair =
// asset.books:write (over only asset:read — finance ⊥ ops). Write-off is
// finance's own reconciliation rhythm (the Books face, site-level sweeps) —
// never a lifecycle door. Effective dates: calendar days, ≤ today
// (service-enforced with the request clock; the schema pins the format).
//
// THE DOORS ≠ THE VERBS (architect-ruled — ADR-063). The UI exposes
// progressive doors per status (Decommission · Retire · Dispose · Reactivate ·
// Reinstate · Recover…); a door COMPOSES 1..n verbs in one request sharing
// one correlation_id (the wrapper doctrine — verbs stay unceremonious). The
// escalation ladder: every exit door proposes the full death — verdict →
// write-off → disposal — and the actor rides as far as their rights allow in
// ONE act (books fronts render only with asset.books:write).
//
// Verbs record the PAST (effectiveOn ≤ today, always). Future intent — the
// door's assign / schedule arms — is the tickets wave: a work order holds it
// and fires the SAME verb via the work_item caller context at completion.
// The register records the past; work orders hold the future.
// ── Caller context — WHO is invoking the verb (the slot that lets tickets
// arrive without redesign). Mirrors the audit 3-actor encoding: user (web) ·
// api_client (+ onBehalfOfUserId = the bot-mediated human) · work_item
// (reserved — arrives with tickets; carries the executing human).
export const CallerContextSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('user'), userId: z.string().min(1).max(16) }),
    z.object({
        kind: z.literal('api_client'),
        apiClientId: z.string().min(1).max(16),
        onBehalfOfUserId: z.string().min(1).max(16).optional(),
    }),
    z.object({
        kind: z.literal('work_item'),
        workItemId: z.string().min(1).max(20),
        userId: z.string().min(1).max(16),
    }),
]);
const dateOnly = z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const assetId = z.string().min(1).max(16);
// Where a thing sits: exactly one of the two location kinds (the XOR law).
// `site_storage` is the third INPUT spelling — the site's own derived storage
// warehouse, addressed by its host (ADR-066: derived rows have no identity of
// their own); the service ensures the row on demand and resolves it to a
// warehouse target, so callers never hold a storage warehouse id.
export const AssetLocationSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('site'), siteId: z.string().min(1).max(16) }),
    z.object({ kind: z.literal('warehouse'), warehouseId: z.string().min(1).max(16) }),
    z.object({ kind: z.literal('site_storage'), siteId: z.string().min(1).max(16) }),
]);
const eventStory = {
    effectiveOn: dateOnly,
    reasonType: AssetEventReasonTypeSchema.optional(),
    note: z.string().trim().min(1).max(2000).optional(),
};
// Books-facet fields (written only through the books door; on create they are
// accepted only when the caller also holds asset.books:write).
export const AcquisitionPatchSchema = z
    .object({
    ownerId: z.string().min(1).max(16).nullable().optional(),
    acquiredOn: dateOnly.nullable().optional(),
    acquiredCondition: z.enum(['new', 'used']).nullable().optional(),
    // Money carries its currency (common/currency): the cost counts the currency's minor units at most — judged here
    // when both travel, by the verb against the stored pair when one does.
    acquisitionCost: z.number().positive().nullable().optional(),
    acquisitionCurrency: CurrencyCodeSchema.nullable().optional(),
    acquisitionMode: z.enum(['purchased', 'leased', 'customer_provided', 'other']).nullable().optional(),
    supplier: z.string().trim().min(1).max(64).nullable().optional(),
    warrantyEndOn: dateOnly.nullable().optional(),
})
    .superRefine((v, ctx) => refineMoney(v.acquisitionCost, v.acquisitionCurrency, ctx, 'acquisitionCost'));
// Fact fields the create verb accepts and the edit verb patches (the record
// facts — identity, structure, rating, access, health, reservation). Location
// and status NEVER move through edit — that is what the lifecycle verbs are.
export const AssetFactsSchema = z.object({
    assetModelId: z.string().min(1).max(16).nullable().optional(),
    serialNumber: z.string().trim().min(1).max(64).nullable().optional(),
    name: z.string().trim().min(1).max(64).nullable().optional(),
    reference: z.string().trim().min(1).max(48).nullable().optional(),
    referenceAlt: z.string().trim().min(1).max(48).nullable().optional(),
    parentAssetId: assetId.nullable().optional(),
    unitCount: z.number().int().positive().nullable().optional(),
    reservedForSiteId: z.string().min(1).max(16).nullable().optional(),
    ratingValue: z.number().positive().nullable().optional(),
    ratingUnit: RatingUnitSchema.nullable().optional(),
    deviceIp: IpAddressSchema.nullable().optional(),
    // The addressing edge (ADR-063): the unit this one is dialed THROUGH. The
    // verb fence: same project · network-capable target · no self-ref · no
    // cycles (`fronts` is picker vocabulary, never a fence).
    accessedViaAssetId: assetId.nullable().optional(),
    deviceAccess: DeviceAccessWriteSchema.nullable().optional(),
    stateOfHealthPct: z.number().min(0).max(100).nullable().optional(),
    stateOfHealthOn: dateOnly.nullable().optional(),
    // The 4-grade verdict cache + its found-on day (the SOH pair's sibling —
    // doors write it via the event condition axis; standalone corrections ride
    // edit/audit per the 2026-08-04 ruling, and this create/edit slot is that
    // path). condition_on without a condition is rejected service-side.
    condition: AssetConditionGradeSchema.nullable().optional(),
    conditionOn: dateOnly.nullable().optional(),
});
// ── create — born anywhere (event: created). The 30-second add: category +
// location + status; everything else optional (completeness is a chip).
export const CreateAssetInputSchema = z
    .object({
    category: AssetCategorySchema,
    status: z.enum(['stocked', 'deployed', 'on_hold', 'retired', 'lost']),
    location: AssetLocationSchema.nullable(), // NULL only for retired/lost with no known place
    facts: AssetFactsSchema.optional(),
    acquisition: AcquisitionPatchSchema.optional(), // books-gated on create
    ...eventStory, // effectiveOn = since when it has been there
})
    // The reference is the register's own KEY — MANDATORY at birth (2026-08-09
    // ruling; the UI's suggest convenience feeds it, the reconciliation engine's
    // machine births generate at accept). Enforced here so every caller shares the rule.
    .superRefine((v, ctx) => {
    if (!v.facts?.reference || v.facts.reference.trim().length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['facts', 'reference'], message: 'reference_required' });
    }
});
// ── move — location change (event: moved; one event per asset; container
// cascades share the correlation). A move may also change status en route
// (pull-out: deployed@site → stocked@depot) — still ONE event. 'retired' is
// legal only status-preserving (relocating retired stock — scrap
// consolidation; service-validated: current status must already be retired).
// `includeChildren` = "Move its parts too", ticked by default: the parts
// standing with the container follow it; unticked, they stay where they are
// and leave it (a part sits where its container is — ADR-063).
export const MoveAssetInputSchema = z.object({
    assetId,
    to: AssetLocationSchema,
    resultingStatus: z.enum(['deployed', 'on_hold', 'stocked', 'retired']), // validated vs the status×location law
    includeChildren: z.boolean().default(true),
    ...eventStory,
});
// ── replace — the flagship one-screen composite. One row per touched asset
// (architect-ruled): outgoing always `replaced`; incoming `replaced` when it
// already exists (old shelf → the slot), `created`-with-counterpart when born
// right there — never a created+replaced double row. One correlation across
// both rows. The outgoing may be deployed OR on_hold (the failed-unit-went-
// on_hold-awaiting-spares arrival keeps its swap pairing); the incoming
// existing unit must be stocked or on_hold (a deployed unit serves a slot —
// pull it first). Incoming takes the slot: outgoing's location, deployed,
// parent inherited.
// The CARRY-OVER door (ADR-063 rev — partial porting, default-on UI-side):
// replacements are configured identically (same VPN config, re-seated SIM), so
// the door offers the outgoing unit's connectivity to the incoming one.
// Secrets copy SERVER-SIDE as ciphertext, never via the wire; an unticked
// secret lands as *secret not set* and the `credential_gap` family nags it.
// `repointFronted` re-points the units dialed through the outgoing unit at the
// incoming one (default-on UI-side; `via_unreachable` is the net for every
// other exit path).
export const ReplaceCarryOverSchema = z
    .object({
    /** Carry `device_ip` (the overlay/collection address — or the local one). */
    address: z.boolean().default(false),
    /** Carry the `accessed_via` edge (dropped server-side if it would self-ref the incoming). */
    edge: z.boolean().default(false),
    /** Carry the SIM slot records (the re-seated SIM). */
    cellular: z.boolean().default(false),
    /** Per service entry: carry its non-secret settings; tick secret FIELDS to
     *  copy their sealed values (per-kind defaults are a UI concern). */
    services: z
        .array(z
        .object({
        // Derived from the access registry (never `cellular` — its SIM slot
        // records carry no secret fields and ride the `cellular` flag above);
        // a newly-admitted kind is carriable the day it lands, no edit here.
        kind: DeviceAccessKindSchema.exclude(['cellular']),
        secretFields: z.array(z.string().trim().min(1).max(32)).default([]),
    })
        .strict())
        .max(DEVICE_ACCESS_KINDS.length - 1)
        .refine((svcs) => new Set(svcs.map((s) => s.kind)).size === svcs.length, {
        message: 'one carry-over entry per service kind',
    })
        .default([]),
    repointFronted: z.boolean().default(false),
})
    .strict();
export const ReplaceAssetInputSchema = z.object({
    outgoingAssetId: assetId,
    outgoingDisposition: z.object({
        status: z.enum(['stocked', 'on_hold', 'retired', 'lost']),
        location: AssetLocationSchema.nullable(), // where the pulled unit goes (null: lost/retired-nowhere)
        conditionFound: AssetConditionGradeSchema.optional(), // the pulled unit's finding → the event condition axis
    }),
    incoming: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('existing'), assetId }),
        z.object({ kind: z.literal('new'), create: z.object({ category: AssetCategorySchema, facts: AssetFactsSchema.optional() }) }),
    ]),
    carryOver: ReplaceCarryOverSchema.optional(),
    ...eventStory,
});
// ── adjust — real-world count change on an aggregate (event: adjusted).
export const AdjustAssetInputSchema = z.object({
    assetId,
    unitCountDelta: z.number().int().refine((n) => n !== 0, 'delta cannot be zero'),
    ...eventStory,
});
// ── edit — fact patch; the ONE verb with NO event (audit kind 'edited'
// carries the diff — the split test: no real-world moment). End-of-books date
// repair is NOT here (the cache-follows-correction law): every effective-date
// fix single-doors through the event-date correction verb, which re-derives
// the row caches — edit never hand-fixes a ledger-cached date. STRICT so a
// stale caller still sending `endOfBooks` gets a loud 400, never a silent
// strip that reads as a landed correction.
export const EditAssetInputSchema = z
    .object({
    assetId,
    facts: AssetFactsSchema.optional(),
    acquisition: AcquisitionPatchSchema.optional(), // books-gated
})
    .strict();
// ── lifecycle: the in-place service flip (no location field by design — a
// relocating return/exit is `move`). `conditionFound` = the door-collected
// finding riding the event condition axis (ADR-063 rev 2026-08-04) — declare-
// faulty's grade, decide's after-inspection grade.
// putOnHold: deployed/stocked → on_hold (event: put_on_hold).
export const PutOnHoldInputSchema = z.object({
    assetId,
    conditionFound: AssetConditionGradeSchema.optional(),
    ...eventStory,
});
// reactivate: on_hold → deployed (at a site) / stocked (in a warehouse) —
// the resulting status derives from the location kind; nothing to ask.
export const ReactivateAssetInputSchema = z.object({
    assetId,
    conditionFound: AssetConditionGradeSchema.optional(),
    ...eventStory,
});
// ── lifecycle: retire / reinstate / declareLost / recover.
// retire = the end-of-life verdict (event: retired). Optional `to` = the
// pull-out destination recorded on the SAME event's location pair (one
// real-world moment, one row); omitted = stays in place (the graveyard is
// real, on sites and shelves alike). With `to`, `includeChildren` = "Move its
// parts too" (ticked by default): the parts standing with it are retired with
// it, at the same place (retired / lost parts keep their status and follow);
// unticked, they stay where they are and leave it.
export const RetireAssetInputSchema = z.object({
    assetId,
    to: AssetLocationSchema.optional(),
    includeChildren: z.boolean().default(true),
    conditionFound: AssetConditionGradeSchema.optional(),
    ...eventStory,
});
// reinstate = the verdict reversal (event: reinstated): retired → live.
// Service-blocked once disposed (physically gone — a bought-back unit is a NEW
// register row). Books facts stand untouched: a written-off unit back in
// service is normal fully-depreciated equipment; the digest surfaces it.
// `to` omitted = stays where it sits. The resulting state DEFAULTS to
// on_hold (a reversed verdict warrants triage — architect-ruled);
// stocked/deployed are explicit choices.
export const ReinstateAssetInputSchema = z.object({
    assetId,
    to: AssetLocationSchema.optional(),
    resultingStatus: z.enum(['stocked', 'deployed', 'on_hold']),
    ...eventStory,
});
export const DeclareLostInputSchema = z.object({ assetId, ...eventStory });
// recover — the only ops exit from lost. Defaults in the UI: found where we
// last knew it, restored to its pre-loss status (read from the event stream) —
// 'retired' included, so a lost scrap unit recovers back to the graveyard.
export const RecoverAssetInputSchema = z.object({
    assetId,
    to: AssetLocationSchema,
    resultingStatus: z.enum(['stocked', 'deployed', 'on_hold', 'retired']),
    ...eventStory,
});
// ── dispose (ops, asset:write — retired-only; lost closes via write-off) +
//    the books pair (asset.books:write — over only asset:read; finance ⊥ ops).
export const WriteOffAssetInputSchema = z.object({ assetId, ...eventStory });
export const DisposeAssetInputSchema = z.object({
    assetId,
    method: z.enum(['scrapped', 'sold', 'donated', 'returned']),
    ...eventStory,
});
export const TransferOwnershipInputSchema = z.object({
    assetId,
    toOwnerId: z.string().min(1).max(16),
    ...eventStory,
});
